import { CheckCircleOutline, Close, DoDisturbAlt, InfoOutlined, WarningAmber } from '@mui/icons-material';
import { Box, IconButton, useTheme } from '@mui/material';
import { closeSnackbar, type CustomContentProps, SnackbarContent, type SnackbarKey } from 'notistack';
import React, { type ReactElement } from 'react';

type VivaCustomSnackbarProps = {
  readonly additional?: string | ReactElement;
  readonly hideCloseIcon?: boolean;
} & CustomContentProps;

export const VivaCustomSnackbar = ({
  ref,
  ...props
}: VivaCustomSnackbarProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { id, message, additional, hideCloseIcon, ...other } = props;
  const theme = useTheme();

  const getVariantIcon = (): ReactElement | null => {
    const style = {
      marginRight: '10px',
      width: '20px',
      height: '20px',
      mt: '2px',
    };

    switch (other.variant) {
      case 'success':
        return <CheckCircleOutline sx={{ ...style, color: theme.palette.success.main }} />;
      case 'error':
        return <DoDisturbAlt sx={{ ...style, color: theme.palette.error.main }} />;
      case 'warning':
        return <WarningAmber sx={{ ...style, color: theme.palette.warning.main }} />;
      case 'info':
        return <InfoOutlined sx={{ ...style, color: theme.palette.info.main }} />;
      default:
        return null;
    }
  };

  const closeIcon = (snackbarId: SnackbarKey) =>
    hideCloseIcon ? null : (
      <IconButton
        onClick={() => {
          closeSnackbar(snackbarId);
        }}
        sx={{
          width: '24px',
          height: '24px',
          marginLeft: 'auto',
          color: theme.palette.text.secondary,
          '& svg': {
            fontSize: '14px',
          },
        }}
      >
        <Close />
      </IconButton>
    );

  return (
    <SnackbarContent ref={ref} role="alert" {...other}>
      <Box
        sx={{
          display: 'flex',
          borderRadius: '10px',
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px;',
          padding: '6px 16px 6px 20px',
          minWidth: '350px',
          maxWidth: '480px',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            p: '8px 0px',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              fontWeight: 500,
              width: '100%',
            }}
          >
            {getVariantIcon()}
            {message}
          </Box>

          {closeIcon(id)}
        </Box>
        {additional && (
          <Box
            sx={{
              color: 'grey',
              fontSize: '.95em',
              pb: '10px',
            }}
          >
            {additional}
          </Box>
        )}
      </Box>
    </SnackbarContent>
  );
};
